import { CustomNode, NodeFile, NodeStruct } from './node-types';
import { CustomEdge } from './edge-types';

export interface WorkflowMetadata {
  asset_mode?: 'public' | 'private';
  /**
   * Workflow-level special instructions. Appended by the backend to the
   * system prompt of every agent/LLM node during workflow execution.
   */
  special_instructions?: string;
  // Workflow default for mid-run clarifications (precedence: explicit > workflow default > off).
  // Unset = runs follow the user's personal/backend default.
  default_clarification_mode?: 'auto' | 'ask_with_timeout' | 'ask_and_wait';
  // Only meaningful when default_clarification_mode is 'ask_with_timeout'.
  default_clarification_timeout_secs?: number;
}

// read: reuse existing code only. read_write: also commit back, and only when
// the run is started by the workflow owner.
export type SoftwareRepoMode = 'read' | 'read_write';

export interface Workflow {
  id: string;
  session_id: string;
  publish_to_template: boolean;
  publish_to_api?: boolean;
  publish_to_mcp?: boolean;
  software_repo_url?: string | null;
  software_repo_branch?: string | null;
  software_repo_mode?: SoftwareRepoMode | null;
  api_code?: string;
  mcp_code?: string;
  /** Real MCP gateway endpoint persisted at publish time (null/absent when
   * the workflow isn't wired into the org's Published MCP server). */
  mcp_endpoint?: string | null;
  name: string;
  nodes: CustomNode[];
  edges: CustomEdge[];
  createdAt?: string;
  screenshot?: string; // Base64 encoded screenshot data
  description?: string;
  icon?: string;
  color?: string;
  bg_color?: string;
  default_role?: string | null;
  is_locked?: boolean;
  locked_by_id?: string;
  locked_by_name?: string;
  locked_at?: string;
  metadata?: WorkflowMetadata;
  labels?: Label[];
}

export interface Label {
  id: string;
  org_id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  created_at: string;
  updated_at: string;
}

// Workflow Details of each workflow in list view
export interface WorkflowDetails {
  workflow_id: string;
  publish_to_template: boolean;
  publish_to_api?: boolean;
  publish_to_mcp?: boolean;
  name: string;
  created_at: string;
  created_by_name: string;
  created_by_email: string;
  icon: string;
  color: string;
  bg_color: string;
  updated_at: string;
  description: string;
  node_count?: number;
  collaborators?: Array<{
    id: string;
    email?: string | null;
    name?: string | null;
  }>;
  screenshot?: string;
  labels?: Label[];
  user_role?: string | null;
  default_role?: string | null;
  is_locked?: boolean;
  locked_by_name?: string;
  locked_at?: string;
  has_pending_request?: boolean;
  pending_request_count?: number;
  last_run_at?: string | null;
  total_runs?: number;
  latest_run_cost_usd?: number | null;
  has_schedule?: boolean;
  schedule_cron?: string | null;
  schedule_enabled?: boolean | null;
  schedule_next_run_at?: string | null;
  schedule_count?: number;
}

export type WorkflowSortField =
  | 'name'
  | 'created_at'
  | 'updated_at'
  | 'created_by'
  | 'last_run_at'
  | 'total_runs'
  | 'latest_run_cost_usd';

// Workflow Event types
export interface BaseEvent {
  event_type: string;
  timestamp: string;
  event_id: string;
}

// Workflow-level event types
export interface StatusUpdateEvent extends BaseEvent {
  event_type: 'status_update';
  message: string;
  data: Record<string, unknown>;
  node_id: string;
  display_name: string;
}

export interface TerminationEvent extends BaseEvent {
  event_type: 'termination';
  status_code: number;
}

export interface WorkflowResult {
  execution_summary: {
    total_nodes: number;
    // Newer backends emit structured ``NodeError`` objects; older ones (and
    // some summaries) still emit plain strings, so accept both.
    errors: Array<string | NodeError>;
    execution_order: string[];
    entry_nodes: string[];
  };
  message: string;
  session_id: string;
  success: boolean;
  workflow: {
    edges: CustomEdge[];
    nodes: NodeStruct[];
  };
  description: string;
  edges: string[];
  entry_node_id: string;
  execution_metadata: string;
  global_context: string;
  id: string;
  metadata: string;
  name: string;
  nodes: string[];
  websocket_url: string;
}

export interface ExecutionResultEvent extends BaseEvent {
  event_type: 'execution_result';
  message: string;
  data: WorkflowResult;
}

export interface ChatStatusEvent extends BaseEvent {
  event_type: 'chat_status';
  message: string;
  data: {
    content: string;
    node_id: string;
    react_flow_generated_id: string;
    session_id: string;
    workflow_id: string;
    slides_url: string;
  };
}

export interface LiveTranscriptData {
  sequence: number;
  text: string;
  is_final: boolean;
  speaker: string | null;
  elapsed_seconds: number;
}

export interface LiveTranscriptEvent extends BaseEvent {
  event_type: 'live_transcript';
  node_id: string;
  data: LiveTranscriptData;
}

export interface NodeResultData {
  field_id: string;
  display_name: string;
  display_modal: string;
  valid_types: string[];
  show: boolean;
  advanced: boolean;
  required: boolean;
  info: string;
  value: string | null;
  is_list: boolean;
  hidden: boolean;
  files?: NodeFile[];
}

export interface NodeResultDataEvent extends BaseEvent {
  event_type: 'node_result';
  node_id: string;
  node_display_name: string;
  display_type: 'node_result';
  dynamic_message: string;
  data: NodeResultData;
  mode: string;
  /** Set by the WS shaper when any string in the payload was clipped or
   *  nulled due to the WS content cap. */
  content_truncated?: boolean;
}

export enum NodeEventType {
  BEGIN = 'begin',
  COMPILED = 'compiled',
  PROCESSING = 'processing',
  EXECUTION_SUCCESSFUL = 'execution_successful',
  EXECUTION_FAILED = 'execution_failed',
  END = 'end',
  AWAITING_USER_INPUT = 'awaiting_user_input',
  USER_INPUT_RECEIVED = 'user_input_received',
  AWAITING_INTERVENTION = 'awaiting_intervention',
  NODE_RESULT = 'node_result',
  // Emitted by the BE activity wrapper between failed attempts when
  // the exception was classified as retryable AND the activity has
  // more attempts available per its RetryPolicy.  The FE uses it to
  // render an amber "Retrying..." state instead of letting the prior
  // failed attempt's emit_error keep the node stuck on red.  Payload
  // (in event.data): { attempt, max_attempts, retry_in_seconds,
  // error_code, user_message }.
  RETRY_SCHEDULED = 'retry_scheduled',
}

export enum NodeDataType {
  CODE = 'code',
  MARKDOWN = 'markdown',
  JSON = 'json',
  TOOL_CALL = 'tool_call',
  TOOL_RESULT = 'tool_result',
  THINKING = 'thinking',
  NODE_RESULT = 'node_result',
  UPDATE = 'update',
  ERROR = 'error',
  CHAT_REQUEST = 'chat_request',
  CHAT_EVENT = 'chat_event',
  CHAT_RESPONSE = 'chat_response',
  AGENT_MESSAGE = 'agent_message',
  CHAT_AGENT_MESSAGE = 'chat_agent_message',
  INTERVENTION_REQUEST = 'intervention_request',
}

export enum StreamType {
  TEXTSTREAM = 'textstream',
  BLOCK = 'block',
}

// ─────────────────────────────────────────────────────────────────────
// Structured error info
// ─────────────────────────────────────────────────────────────────────
//
// These types mirror ``temporal/errors/error_info.py`` in the
// agentic-backend repo.  They arrive on:
//
// * ``execution_summary.errors[i].error_info`` in the EXECUTION_RESULT
//   workflow event,
// * ``node_result.error_info`` in the persisted node result.
//
// Frontend code switches on ``code`` for precise messaging and on
// ``category`` for which banner colour / icon to show.  Anything
// unrecognised should fall through to ``ErrorCode.UNKNOWN`` /
// ``ErrorCategory.INTERNAL`` -- the union types below are open
// (``string`` fallback) on purpose so a backend addition doesn't
// break the build before the matching FE update lands.

/**
 * Why the error happened, from the user's point of view.  Drives
 * which banner colour / icon / call-to-action the FE shows.
 */
export enum ErrorCategory {
  /** Upstream LLM / API failure -- not a product bug. */
  EXTERNAL_SERVICE = 'external_service',
  /** Upstream rate-limit or quota. */
  RATE_LIMIT = 'rate_limit',
  /** The submitted input is the problem (bad prompt, content policy, etc). */
  USER_INPUT = 'user_input',
  /** Workspace/account config (invalid key, exhausted quota, missing perms). */
  CONFIGURATION = 'configuration',
  /** Our code or infrastructure. */
  INTERNAL = 'internal',
  /** User-initiated stop / cancellation. */
  CANCELLED = 'cancelled',
}

/**
 * Stable machine-readable code for the specific failure.  More
 * granular than ``ErrorCategory``; the FE can use this to show a
 * precise message ("Your Anthropic key is invalid") instead of the
 * generic category banner.
 */
export enum ErrorCode {
  RATE_LIMIT = 'rate_limit',
  QUOTA_EXCEEDED = 'quota_exceeded',
  SERVER_ERROR = 'server_error',
  TIMEOUT = 'timeout',
  CONNECTION = 'connection',
  RESPONSE_INVALID = 'response_invalid',
  UNEXPECTED_BEHAVIOR = 'unexpected_behavior',
  BAD_REQUEST = 'bad_request',
  CONTENT_POLICY = 'content_policy',
  CONTEXT_LENGTH_EXCEEDED = 'context_length_exceeded',
  VALIDATION = 'validation',
  AUTH_INVALID = 'auth_invalid',
  PERMISSION_DENIED = 'permission_denied',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  UNKNOWN = 'unknown',
}

/** Upstream service that produced the error. */
export enum ErrorProvider {
  ANTHROPIC = 'anthropic',
  OPENAI = 'openai',
  OPENROUTER = 'openrouter',
  GEMINI = 'gemini',
  PYDANTIC_AI = 'pydantic_ai',
  PLATFORM_API = 'platform_api',
  UNKNOWN = 'unknown',
}

/**
 * Structured error description for one failed node.
 *
 * Backend builds this in ``temporal/errors/error_info.py``.  Every
 * field is optional from the FE's perspective so a mid-deploy where
 * the BE is older than the FE still renders gracefully.
 */
export interface ErrorInfo {
  code: ErrorCode | string;
  category: ErrorCategory | string;
  provider: ErrorProvider | string;
  retryable: boolean;
  attempts_made: number;
  user_message: string;
  technical_message: string;
  actionable_hint?: string | null;
  http_status?: number | null;
  request_id?: string | null;
}

/**
 * One element of ``WorkflowResult.execution_summary.errors``.  The
 * legacy ``error`` / ``type`` fields stay for backward compat; new
 * code should prefer ``error_info`` when present.
 */
export interface NodeError {
  node_id?: string;
  display_name?: string;
  error?: string;
  type?: string;
  error_info?: ErrorInfo | null;
}

export interface ChatRequestData {
  request_id: string;
  initial_message: string;
  input_type: string;
  options: string[];
  timeout: number;
  metadata: Record<string, unknown>;
}

export interface ChatResponseData {
  request_id: string;
  user_input: string;
  timestamp: string;
}

// Node-level event types
export interface NodeEvent extends BaseEvent {
  node_id: string;
  node_display_name: string;
  event_type: NodeEventType;
  display_type: NodeDataType;
  dynamic_message: string;
  data: string | Record<string, unknown> | unknown[] | ChatRequestData;
  mode: StreamType;
  timestamp: string;
  event_id: string;
  block_id: string;
}

// Specific node event interfaces for better type safety
export interface NodeBeginEvent extends NodeEvent {
  event_type: NodeEventType.BEGIN;
  display_type: NodeDataType.UPDATE;
}

export interface NodeUpdateEvent extends NodeEvent {
  event_type: NodeEventType.PROCESSING;
  display_type: NodeDataType.UPDATE;
}

export interface NodeAwaitingUserInputEvent extends NodeEvent {
  event_type: NodeEventType.AWAITING_USER_INPUT;
  display_type: NodeDataType.CHAT_REQUEST;
  data: ChatRequestData;
}

export interface NodeUserInputReceivedEvent extends NodeEvent {
  event_type: NodeEventType.USER_INPUT_RECEIVED;
  display_type: NodeDataType.CHAT_RESPONSE;
}

export interface NodeResultEvent extends NodeEvent {
  event_type: NodeEventType.EXECUTION_SUCCESSFUL;
  display_type: NodeDataType.NODE_RESULT;
}

export interface NodeFailedEvent extends NodeEvent {
  event_type: NodeEventType.EXECUTION_FAILED;
  display_type: NodeDataType.ERROR;
}

export interface NodeEndEvent extends NodeEvent {
  event_type: NodeEventType.END;
  display_type: NodeDataType.UPDATE;
}

export interface ToolCallData {
  tool_name: string;
  tool_arguments: string;
}

export interface ToolCallResult {
  tool_name: string;
  tool_result: string;
}

export enum TerminationStatusCode {
  SUCCESS = 0,
  ERROR = 100,
  STOPPED = 200,
}

export enum AutoSaveStatus {
  IDLE = 'idle',
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
}

export enum WorkflowExecutionUiStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  // PAUSED is a non-terminal state: the workflow is alive on the
  // backend (parked on a Temporal wait_condition) and survives
  // browser sessions.  The user can edit the canvas in this state;
  // resume flushes any edits to the backend before continuing.
  PAUSED = 'paused',
  // PAUSING / RESUMING are transient client-side affordances for
  // optimistic feedback before the lifecycle WS event lands.
  PAUSING = 'pausing',
  RESUMING = 'resuming',
  COMPLETED = 'completed',
  FAILED = 'failed',
  RECONNECTING = 'reconnecting',
  CHECKING_STATUS = 'checking_status',
}

// Legacy enum for backward compatibility
export enum WorkflowEventTypes {
  STATUS_UPDATE = 'status_update',
  TERMINATION = 'termination',
  EXECUTION_RESULT = 'execution_result',
  CHAT_STATUS = 'chat_status',
  NODE_RESULT = 'node_result',
  // Run-level lifecycle transition (pause / resume / edit / rerun).
  // Carries a ``phase`` discriminator + free-form ``data`` payload.
  // The backend emits these from its persistence activities; the FE
  // uses them to flip the status bar instantly without polling.
  LIFECYCLE = 'lifecycle',
  LIVE_TRANSCRIPT = 'live_transcript',
  // Node event types
  NODE_BEGIN = 'begin',
  NODE_COMPILED = 'compiled',
  NODE_PROCESSING = 'processing',
  NODE_EXECUTION_SUCCESSFUL = 'execution_successful',
  NODE_EXECUTION_FAILED = 'execution_failed',
  NODE_END = 'end',
}

/**
 * Sub-types carried on a LIFECYCLE event.  Distinguished from
 * EventType so the FE can switch on event_type first, then on phase
 * for the specific transition.  Open-ended on purpose so a backend
 * addition doesn't break the build before the matching FE update lands.
 */
export enum LifecyclePhase {
  /** Workflow transitioned to PAUSED.  data carries pause_state. */
  PAUSED = 'paused',
  /** Workflow transitioned back to RUNNING after a resume signal. */
  RESUMED = 'resumed',
  /**
   * Pause-time edit was materialised: new RUN version created,
   * NodeResult rows for the invalidated set deleted.  data includes
   * new_version_id, new_version_number, invalidated_node_ids,
   * deleted_result_count.
   */
  EDIT_APPLIED = 'edit_applied',
  /**
   * Rerun-from-node has been picked up by the worker and the cached-
   * vs-fresh split has been computed.  Emitted at the START of the
   * seed activity so the FE can paint the teal cached badges
   * immediately, rather than waiting for the seed loop to finish.
   * data shape mirrors RERUN_SEEDED (cached_node_ids, fresh_node_ids,
   * source_session_id, source_run_id, cached_node_count).
   */
  RERUN_STARTED = 'rerun_started',
  /**
   * Rerun-from-node seeding finished.  data includes source_session_id,
   * source_run_id, cached_node_count, cached_node_ids, fresh_node_ids.
   */
  RERUN_SEEDED = 'rerun_seeded',
  // Workflow parked awaiting deliver_clarification or the timeout. data:
  // asker_node_id, display_name, question, context, timeout_secs.
  CLARIFICATION_REQUESTED = 'clarification_requested',
  // data: asker_node_id, timed_out (true for the synthetic timeout fallback).
  CLARIFICATION_RESOLVED = 'clarification_resolved',
  // data.paused_at_epoch_ms is when the BE recorded the pause, so a reload can
  // render the frozen countdown at the right remaining-time.
  CLARIFICATION_COUNTDOWN_PAUSED = 'clarification_countdown_paused',
  // data.deadline_epoch_ms is already shifted, so the FE re-anchors without
  // recomputing the pause delta; data.forced marks a safety-cap auto-resume.
  CLARIFICATION_COUNTDOWN_RESUMED = 'clarification_countdown_resumed',
}

/** State snapshot persisted on workflow_run.pause_state at pause time. */
export interface PauseStateSnapshot {
  paused_at: string;
  paused_by_user_id: string | null;
  completed_node_ids: string[];
  in_flight_at_pause: string[];
  version_history: string[];
  notes?: string | null;
}

/**
 * Lifecycle event delivered on the same WS channel as the other
 * workflow events.  Event handlers should switch on
 * ``phase`` (open-string fallback recommended).
 */
export interface LifecycleEvent extends BaseEvent {
  event_type: 'lifecycle';
  phase: LifecyclePhase | string;
  message: string;
  data: Record<string, unknown>;
}

// Enums matching backend
export enum EventType {
  IN_PROGRESS = 'in_progress',
  STATUS_UPDATE = 'status_update',
  TERMINATION = 'termination',
  EXECUTION_RESULT = 'execution_result',
  LIFECYCLE = 'lifecycle',
  NODE_BEGIN = 'begin',
  NODE_COMPILED = 'compiled',
  NODE_PROCESSING = 'processing',
  NODE_EXECUTION_SUCCESSFUL = 'execution_successful',
  NODE_EXECUTION_FAILED = 'execution_failed',
  NODE_END = 'end',
}

// Union type for all possible events
export type NodeStatusEvent =
  | StatusUpdateEvent
  | TerminationEvent
  | ExecutionResultEvent
  | ChatStatusEvent
  | NodeResultDataEvent
  | LiveTranscriptEvent
  | LifecycleEvent
  | NodeEvent
  | NodeBeginEvent
  | NodeUpdateEvent
  | NodeFailedEvent
  | NodeEndEvent;
